import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useMotionEnabled from '../../hooks/useMotionEnabled';
import { useMeadowTheme } from '../../hooks/MeadowThemeContext';
import useThemeMural from '../../hooks/useThemeMural';
import {
  heroSection,
  meadowCases,
  playSection,
  aboutSection,
  contactSection,
  resumeSection,
} from '../../data/homeContent';
import muralAlpineDay from '../../assets/home/mural-alpine-day.webp';
import muralAlpineDusk from '../../assets/home/mural-alpine-dusk.webp';
import HomeBirds from '../../components/HomeBirds/HomeBirds';
import Footer from '../../components/Footer/Footer';
import MeadowCaseCard from '../../components/MeadowCaseCard/MeadowCaseCard';
import './Home.css';
import './HomeMotion.css';
import { isFigmaCapture } from '../../utils/figmaCapture';

const HERO_INTRO_KEY = 'home-meadow-intro-played';

const hasPlayedIntro = () => {
  try {
    return sessionStorage.getItem(HERO_INTRO_KEY) === '1';
  } catch {
    return false;
  }
};

const markIntroPlayed = () => {
  try {
    sessionStorage.setItem(HERO_INTRO_KEY, '1');
  } catch {
    // Private mode / blocked storage
  }
};


const TimelineEntry = ({ entry, index, motion }) => {
  const { when, current, kind, role, summary } = entry;
  const ref = useRef(null);
  /* Figma html-to-design skips opacity:0 nodes — keep entries painted for capture */
  const [visible, setVisible] = useState(!motion || isFigmaCapture);

  useEffect(() => {
    if (!motion || isFigmaCapture || !ref.current) return undefined;
    const node = ref.current;
    const io = new IntersectionObserver(
      ([obs]) => {
        if (obs.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px 15% 0px', threshold: 0.05 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [motion]);

  return (
    <li
      ref={ref}
      className={[
        'home-tl',
        current && 'home-tl--current',
        kind === 'study' && 'home-tl--study',
        kind === 'next' && 'home-tl--next',
        !summary && 'home-tl--compact',
        motion && 'home-tl--reveal',
        visible && 'home-tl--in',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tl-i': index }}
    >
      <div className="home-tl__above">
        <p className="home-tl__when">{when}</p>
      </div>

      <div className="home-tl__rail" aria-hidden="true">
        <span className="home-tl__node" />
      </div>

      <div className="home-tl__below">
        <h3 className="home-tl__role">{role}</h3>
        {summary ? <p className="home-tl__summary">{summary}</p> : null}
      </div>
    </li>
  );
};

const PlayCard = ({ item }) => {
  const { title, note, image, imageAlt, paint, to, comingSoon } = item;
  const Wrapper = to && !comingSoon ? Link : 'div';
  const wrapperProps = to && !comingSoon ? { to } : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={['home-play', comingSoon && 'home-play--soon']
        .filter(Boolean)
        .join(' ')}
      style={paint ? { '--play-paint': `url(${paint})` } : undefined}
      {...(comingSoon
        ? { role: 'group', 'aria-label': title }
        : {})}
    >
      <div className="home-play__frame" aria-hidden={comingSoon ? true : undefined}>
        {comingSoon ? null : (
          <img
            className="home-play__media"
            src={image}
            alt={imageAlt || ''}
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
      <h3 className="home-play__title">{title}</h3>
      {note ? <p className="home-play__note">{note}</p> : null}
    </Wrapper>
  );
};

const TIMELINE_VERTICAL_MQ = '(max-width: 900px)';

const Home = () => {
  const motion = useMotionEnabled();
  const { dark } = useMeadowTheme();
  const mural = useThemeMural(dark);
  const skipIntro = !motion || hasPlayedIntro() || isFigmaCapture;
  const [ready, setReady] = useState(skipIntro);
  const [timelineVertical, setTimelineVertical] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(TIMELINE_VERTICAL_MQ).matches;
  });

  // The hero text is absolutely placed, so it cannot push the next section down.
  // Publish where it really ends, so the gap to the first card survives any wrap.
  useLayoutEffect(() => {
    const hero = document.querySelector('.home__hero');
    const text = document.querySelector('.home__hero-inner');
    if (!hero || !text) return undefined;
    const measure = () => {
      const overflow = text.offsetTop + text.offsetHeight - hero.offsetHeight;
      hero.parentElement.style.setProperty('--hero-text-overflow', `${overflow}px`);
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(hero);
    watch.observe(text);
    return () => watch.disconnect();
  }, []);

  useEffect(() => {
    if (!motion || hasPlayedIntro()) {
      setReady(true);
      return undefined;
    }
    markIntroPlayed();
    const t = window.setTimeout(() => setReady(true), 40);
    return () => window.clearTimeout(t);
  }, [motion]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(TIMELINE_VERTICAL_MQ);
    const sync = () => setTimelineVertical(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const timelineEntries = timelineVertical
    ? [...resumeSection.entries].reverse()
    : resumeSection.entries;

  const mainClass = [
    'home',
    'home--meadow',
    dark && 'home--asleep',
    motion && 'home--motion',
    ready && 'home--ready',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <main className={mainClass} id="top" tabIndex={-1}>
      <section
        className="home__hero"
        aria-labelledby="hero-heading"
      >
        <div className="home__hero-stage">
          <div className="home__hero-paint" aria-hidden="true">
            <img
              className="home__hero-mural home__hero-mural--day"
              src={mural.day ? muralAlpineDay : undefined}
              fetchPriority={dark ? 'low' : 'high'}
              alt=""
              draggable={false}
            />
            <img
              className="home__hero-mural home__hero-mural--dusk"
              src={mural.dusk ? muralAlpineDusk : undefined}
              fetchPriority={dark ? 'high' : 'low'}
              alt=""
              draggable={false}
            />
            {motion ? <HomeBirds /> : null}
          </div>
          <div className="home__hero-inner">
            <div className="home__intro">
              <h1 id="hero-heading" className="home__headline">
                <span className="home__headline-line">
                  {heroSection.headlineBefore}
                  <span className="home__headline-accent">
                    {heroSection.headlineAccent}
                  </span>
                  {heroSection.headlineAfter}
                </span>{' '}
                <span className="home__headline-line">
                  {heroSection.headlineLine2}
                </span>
              </h1>
            </div>
            <p className="home__lede">{heroSection.intro}</p>
          </div>
        </div>
      </section>

      <section
        className="home__work"
        id="work"
        aria-labelledby="work-heading"
      >
        {/* Not shown; keeps the page's heading order for screen readers */}
        <h2 id="work-heading" className="visually-hidden">
          Selected work
        </h2>
        <div className="home__bento" role="group" aria-label="Case studies">
          {meadowCases.map((item, i) => (
            <MeadowCaseCard key={item.id} item={item} index={i} />
          ))}
        </div>
      </section>

      {/* Play stays hidden until at least one item is live (no "Coming soon" tiles) */}
      {playSection.items.some((item) => !item.comingSoon) ? (
      <section className="home__play" id="play" aria-labelledby="play-heading">
        <div className="home__section-head">
          <h2 id="play-heading" className="home__section-title">
            {playSection.label}
          </h2>
          <p className="home__section-lede">{playSection.lede}</p>
        </div>
        <ul className="home__play-grid">
          {playSection.items.map((item) => (
            <li key={item.id} className="home__play-item">
              <PlayCard item={item} />
            </li>
          ))}
        </ul>
      </section>
      ) : null}

      <section className="home__about" id="about" aria-labelledby="about-heading">
        <div className="home__about-inner">
          <div className="home__about-copy">
            <h2 id="about-heading" className="home__section-title">
              {aboutSection.title}
            </h2>
            <p className="home__about-lead">{aboutSection.lead}</p>
            {aboutSection.paragraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 32)} className="home__about-body">
                {paragraph}
              </p>
            ))}
          </div>
          <img
            className="home__about-portrait"
            src={dark ? aboutSection.headshotNight : aboutSection.headshot}
            alt={aboutSection.headshotAlt}
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      <section
        className="home__resume"
        id="resume"
        aria-labelledby="resume-heading"
      >
        <div className="home__section-head home__section-head--row">
          <div className="home__section-head-copy">
            <h2 id="resume-heading" className="home__section-title">
              {resumeSection.title}
            </h2>
            <p className="home__section-lede">{resumeSection.lede}</p>
          </div>
          <a
            className="home__resume-download"
            href={resumeSection.downloadHref}
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            {resumeSection.downloadLabel}
          </a>
        </div>

        <ol className="home__timeline" aria-label="Career timeline">
          {timelineEntries.map((entry, i) => (
            <TimelineEntry
              key={entry.id}
              entry={entry}
              index={i}
              motion={motion}
            />
          ))}
        </ol>
      </section>

      <section
        className="home__contact"
        id="contact"
        aria-labelledby="contact-heading"
      >
        <h2 id="contact-heading" className="home__contact-title">
          {contactSection.title} {contactSection.lede}
        </h2>
        <div className="home__contact-actions">
          <a href={contactSection.emailHref}>{contactSection.emailLabel}</a>
          <a
            href={contactSection.linkedinHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {contactSection.linkedinLabel}
          </a>
          <a
            href={resumeSection.downloadHref}
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            {contactSection.resumeLabel}
          </a>
        </div>
      </section>

      <Footer asleep={dark} />
    </main>
  );
};

export default Home;
